import type { CSSProperties, ReactNode } from 'react'
import type { SlidePhase } from './useWeekWindow.ts'
import styles from './DayColumn.module.css'

type Edge = 'top' | 'bottom'

const edgeClass = (edge: Edge) =>
	edge === 'top' ? styles.stripTop : styles.stripBottom

// Chrome that caps the day column. Its children set the contents; the strip
// only fixes the height, so swapping one set of controls for another can't
// resize the column and shift every row.
export const Strip = ({
	edge,
	children,
}: {
	edge: Edge
	children: ReactNode
}) => <div className={`${styles.strip} ${edgeClass(edge)}`}>{children}</div>

// The whole strip is the week-navigation button. `prev` caps the top, `next`
// the bottom, matching the direction each moves the column.
export const WeekNavStrip = ({
	dir,
	disabled,
	onClick,
}: {
	dir: 'prev' | 'next'
	disabled: boolean
	onClick: () => void
}) => (
	<button
		type="button"
		className={`${styles.strip} ${edgeClass(
			dir === 'prev' ? 'top' : 'bottom',
		)} ${styles.nav}`}
		onClick={onClick}
		disabled={disabled}
		aria-label={dir === 'prev' ? 'Previous week' : 'Next week'}
	>
		{dir === 'prev' ? '↑ Previous week' : '↓ Next week'}
	</button>
)

type Props = {
	top: ReactNode
	bottom: ReactNode
	phase: SlidePhase
	onSlideEnd: () => void
	// Rows size themselves as a fraction of the column's height, so a caller
	// with no flexing parent to measure — a dialog — passes a CSS length
	// instead. Omitted, the column fills the space its parent grants.
	height?: string
	children: ReactNode
}

// Two strips sandwiching a viewport of day rows, sharing single-line
// dividers. The viewport is a size container: rows are 12.5cqh (1/8) of it,
// and the slide keyframes move by 87.5cqh (7 rows).
export const DayColumn = ({
	top,
	bottom,
	phase,
	onSlideEnd,
	height,
	children,
}: Props) => (
	<div
		className={`${styles.column} ${height ? styles.fixed : ''}`}
		style={
			height
				? ({ '--day-column-height': height } as CSSProperties)
				: undefined
		}
	>
		{top}
		<div className={styles.viewport}>
			<div
				className={`${styles.days} ${
					phase === 'next' ? styles.slidingNext : ''
				} ${phase === 'prev' ? styles.slidingPrev : ''}`}
				onAnimationEnd={onSlideEnd}
			>
				{children}
			</div>
		</div>
		{bottom}
	</div>
)
